'use client'

import { hero } from '@/lib/marketing/content'

type Props = {
  onOpenDemo: () => void
}

export function Hero({ onOpenDemo }: Props) {
  return (
    <section className="relative pt-28 sm:pt-36 pb-8 sm:pb-12 overflow-hidden">
      {/* Background gradients */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Top-left glow */}
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] bg-[#71ED7F]/[0.03] rounded-full blur-[120px]" />
        {/* Bottom-right glow */}
        <div className="absolute -bottom-60 -right-40 w-[500px] h-[500px] bg-[#E4FFE7]/[0.02] rounded-full blur-[100px]" />
        {/* Center subtle glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#71ED7F]/[0.015] rounded-full blur-[150px]" />
      </div>

      <div className="relative max-w-[960px] mx-auto px-4 sm:px-6">
        {/* Text content */}
        <div className="text-center mb-10 sm:mb-14">
          {/* Headline */}
          <h1 className="mb-6 sm:mb-8">
            <span className="block text-[clamp(2.25rem,5vw,4rem)] font-medium leading-[1.1] tracking-tight font-display text-white">
              {hero.headline.line1}
            </span>
            <span className="block text-[clamp(2.25rem,5vw,4rem)] font-medium leading-[1.1] tracking-tight font-display text-white/35">
              {hero.headline.line2}
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-base sm:text-lg text-white/50 max-w-xl mx-auto mb-8 sm:mb-10 leading-relaxed px-2">
            {hero.subheadline}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8">
            <button
              onClick={onOpenDemo}
              className="relative group w-full sm:w-auto"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-[#71ED7F]/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <span className="relative inline-flex items-center justify-center w-full sm:w-auto bg-[#71ED7F] text-[#001716] px-8 sm:px-10 py-3.5 rounded-full text-sm font-semibold tracking-wide hover:shadow-[0_0_45px_-5px_rgba(113,237,127,0.5)] transition-all">
                {hero.cta.primary}
              </span>
            </button>
            <a
              href="#features"
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3.5 rounded-full text-sm font-medium text-white/60 hover:text-white/80 transition-colors"
            >
              {hero.cta.secondary}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </a>
          </div>

          {/* Capability pills */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-2.5">
            {hero.capabilities.map((capability, i) => (
              <div
                key={i}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E4FFE7]/[0.04] border border-[#E4FFE7]/[0.06] text-white/45 text-xs"
              >
                <svg className="w-3.5 h-3.5 text-[#71ED7F]/60" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {capability}
              </div>
            ))}
          </div>
        </div>

        {/* Dashboard Preview - Mission Control */}
        <div className="relative max-w-4xl mx-auto">
          {/* Outer glow */}
          <div className="absolute -inset-4 bg-[#71ED7F]/[0.04] rounded-3xl blur-2xl pointer-events-none" />

          {/* Main dashboard frame */}
          <div className="relative bg-[#001716] rounded-xl sm:rounded-2xl border border-[#E4FFE7]/[0.1] overflow-hidden shadow-[0_24px_64px_-16px_rgba(0,0,0,0.6)]">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E4FFE7]/[0.06] bg-[#001716]">
              <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
                <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
                <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
              </div>
              <div className="flex-1 mx-4">
                <div className="flex items-center gap-2 h-6 bg-[#E4FFE7]/[0.04] rounded-md max-w-xs mx-auto px-3">
                  <svg className="w-3 h-3 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-[10px] text-white/30">members.yourclub.com</span>
                </div>
              </div>
            </div>

            {/* Dashboard content */}
            <div className="p-4 sm:p-6 min-h-[280px] sm:min-h-[380px]">
              {/* Top nav bar */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#71ED7F]/30 to-[#71ED7F]/10 border border-[#71ED7F]/20" />
                  <div className="h-4 w-28 bg-white/10 rounded" />
                </div>
                <div className="flex items-center gap-3">
                  <div className="hidden sm:flex gap-2">
                    <div className="h-8 w-20 bg-[#E4FFE7]/[0.04] rounded-lg" />
                    <div className="h-8 w-20 bg-[#E4FFE7]/[0.04] rounded-lg" />
                    <div className="h-8 w-24 bg-[#71ED7F]/10 border border-[#71ED7F]/20 rounded-lg" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-[#71ED7F]/20 flex items-center justify-center">
                    <span className="text-[#71ED7F] text-xs font-medium">JD</span>
                  </div>
                </div>
              </div>

              {/* Main content grid */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
                {/* Left sidebar - Quick stats */}
                <div className="hidden sm:block sm:col-span-3 space-y-3">
                  <div className="p-3 rounded-xl bg-[#002C2B]/50 border border-[#E4FFE7]/[0.06]">
                    <div className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Today's Bookings</div>
                    <div className="text-2xl font-semibold text-white">12</div>
                    <div className="text-[10px] text-[#71ED7F]">↑ 3 from yesterday</div>
                  </div>
                  <div className="p-3 rounded-xl bg-[#002C2B]/50 border border-[#E4FFE7]/[0.06]">
                    <div className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Utilization</div>
                    <div className="text-2xl font-semibold text-[#71ED7F]">78%</div>
                    <div className="h-1.5 bg-white/10 rounded-full mt-2">
                      <div className="h-full w-[78%] bg-[#71ED7F]/60 rounded-full" />
                    </div>
                  </div>
                  <div className="p-3 rounded-xl bg-[#002C2B]/50 border border-[#E4FFE7]/[0.06]">
                    <div className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Revenue (MTD)</div>
                    <div className="text-xl font-semibold text-white">$4,280</div>
                  </div>
                </div>

                {/* Main content - Calendar/booking grid */}
                <div className="sm:col-span-9 bg-[#002C2B]/40 rounded-xl p-4 border border-[#E4FFE7]/[0.06]">
                  {/* Calendar header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-sm font-medium text-white/70">January 2026</div>
                    <div className="flex gap-1">
                      <div className="w-6 h-6 rounded bg-[#E4FFE7]/[0.04] flex items-center justify-center">
                        <svg className="w-3 h-3 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </div>
                      <div className="w-6 h-6 rounded bg-[#E4FFE7]/[0.04] flex items-center justify-center">
                        <svg className="w-3 h-3 text-white/30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Day headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                      <div key={i} className="text-center text-[10px] text-white/25 font-medium py-1">{d}</div>
                    ))}
                  </div>

                  {/* Calendar grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Empty cells for offset */}
                    {[...Array(3)].map((_, i) => (
                      <div key={`empty-${i}`} className="aspect-square rounded-lg" />
                    ))}
                    {/* Days with varying states */}
                    {[...Array(28)].map((_, i) => {
                      const day = i + 1
                      const isBooked = [2, 5, 8, 9, 12, 15, 18, 19, 22, 25].includes(day)
                      const isToday = day === 15
                      const hasLesson = [5, 12, 19].includes(day)

                      return (
                        <div
                          key={day}
                          className={`aspect-square rounded-lg flex flex-col items-center justify-center text-[10px] sm:text-xs transition-colors ${
                            isToday
                              ? 'bg-[#71ED7F]/20 border border-[#71ED7F]/40 text-[#71ED7F]'
                              : isBooked
                              ? 'bg-[#E4FFE7]/[0.06] text-white/60'
                              : 'bg-[#E4FFE7]/[0.02] text-white/30'
                          }`}
                        >
                          <span>{day}</span>
                          {hasLesson && (
                            <div className="w-1 h-1 rounded-full bg-[#71ED7F] mt-0.5" />
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Floating cards */}
          {/* Notification card - top right */}
          <div className="absolute -top-3 -right-3 sm:top-4 sm:-right-8 animate-float-slow">
            <div className="bg-[#001716]/95 backdrop-blur-xl rounded-xl border border-[#71ED7F]/20 p-3 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.5)] w-44 sm:w-52">
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-[#71ED7F]/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-[#71ED7F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] sm:text-xs font-medium text-white/90 truncate">New Booking</div>
                  <div className="text-[9px] sm:text-[10px] text-white/40">Bay 3 • Today 2pm</div>
                </div>
              </div>
            </div>
          </div>

          {/* Member card - bottom left */}
          <div className="absolute -bottom-2 -left-2 sm:bottom-8 sm:-left-6 animate-float hidden sm:block">
            <div className="bg-[#001716]/95 backdrop-blur-xl rounded-xl border border-[#E4FFE7]/[0.1] p-3 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.5)] w-48">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                  <span className="text-white text-xs font-medium">MJ</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-white/90">Mike Johnson</div>
                  <div className="text-[10px] text-white/40">12 bookings this month</div>
                </div>
              </div>
            </div>
          </div>

          {/* Label */}
          <p className="text-center text-white/20 text-[10px] sm:text-xs mt-4 uppercase tracking-wider">
            Your Dashboard Preview
          </p>
        </div>
      </div>
    </section>
  )
}
